import { select, call, put } from 'redux-saga/effects';

import { entityIdSelector } from '../../selectors/user';
import UserClient from '../../api/UserClient';
import { setAdyenSession, setAdyenSessionError } from '../../redux/app/actions';
import { setSessionStorageItem } from '../../helpers/LocalStorage';

/**
 * Sessions-flow setup-intent creation (ISJ-8831).
 *
 * Posts a metadata-only body to /setup_intents and stores the returned PSP
 * session (id, sessionData, clientKey) in Redux so the Adyen modal can
 * initialize AdyenCheckout({ session, clientKey }).
 *
 * Replaces the legacy "advanced flow" saga (which bundled encrypted card data
 * in the request body and reacted to nextAction for 3DS). Under Sessions,
 * Adyen handles data collection, encryption, and 3DS internally via the
 * session's returnURL.
 *
 * The body includes `paymentMethodType: 'CREDIT_CARD'` to match the existing
 * Stripe setup-intent saga shape (see stripeSetupIntentSaga in
 * src/sagas/app/index.js). The Adyen Sessions session itself enables multiple
 * payment methods (Card, Google Pay) regardless of this field.
 *
 * Reference: docs/superpowers/specs/2026-05-18-isj-8831-fo-adyen-google-pay-design.md §5
 */
export default function* createAdyenSetupIntent() {
    const entityId = yield select(entityIdSelector);
    try {
        const intent = yield call(UserClient.onSetupIntent, {
            entityId,
            paymentMethodType: 'CREDIT_CARD',
            returnURL: `${window.location.origin}/payment-result`,
        });
        // intent shape (PG SetupIntent DTO):
        // { id, pspClientSecret, pspPublishableKey, pspName, pspReference, returnURL, status, ... }
        // Persist entityId/setupIntentId for the 3DS redirect-back recovery
        // path: after Adyen redirects to /payment-result, PaymentHandleResult
        // dispatches requestAdyenSynchronize which reads these from
        // sessionStorage (it has no Redux access to the original intent IDs
        // because the user navigated away and back).
        setSessionStorageItem('entityId', entityId);
        setSessionStorageItem('setupIntentId', intent.id);
        yield put(
            setAdyenSession({
                id: intent.pspReference,
                setupIntentId: intent.id,
                sessionData: intent.pspClientSecret,
                clientKey: intent.pspPublishableKey,
                returnURL: intent.returnURL,
            })
        );
    } catch (error) {
        console.error('createAdyenSetupIntent', error);
        yield put(setAdyenSessionError(error));
    }
}
